import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Check} from '../icons';
import {initialProfileSelection, profileChoices} from './state/learnModel';

/**
 * El perfil en chips: ordena las pistas, no esconde ninguna (D3). Se pueden combinar.
 *
 * - **Primer ingreso** (`profile.chosen: false`): la pregunta, los chips y «Listo». A un Admin le
 *   llega «Liderazgo» marcado, y si quien lo invitó eligió un perfil, ése (el backend lo trae).
 * - **Ya elegido**: «Tu perfil:» y los chips; tocar uno guarda en el acto.
 *
 * Los chips son botones con `aria-pressed`: su estado se lee con el tilde Y el relleno, no sólo con
 * el color (rule `ui-estilos`, «tres señales»).
 */
export function LearnProfile({model, saving, onSave}) {
    const {t} = useTranslation();
    const [selected, setSelected] = useState(() => initialProfileSelection(model));
    const chosen = model.profile.chosen;

    // Lo que contestó el backend manda: una respuesta nueva (o un error que deshizo el cambio) se
    // vuelve a leer. Mientras se elige por primera vez, la selección es local hasta «Listo».
    const rolesKey = model.profile.roles.join(',');
    useEffect(() => {
        if (chosen) setSelected(initialProfileSelection(model));
    }, [chosen, rolesKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (role) => {
        const next = selected.includes(role) ? selected.filter((each) => each !== role) : [...selected, role];
        setSelected(next);
        if (!chosen) return;
        // Si no se guardó, el chip vuelve a lo que tiene el backend: el error no cambia el modelo, así
        // que el efecto de arriba no se entera y el chip quedaba marcado sobre un perfil que no existe.
        Promise.resolve(onSave(next)).then((saved) => {
            if (saved === false) setSelected(initialProfileSelection(model));
        });
    };

    const chips = (
        <span className="ld-profile__chips">
            {profileChoices(model.isAdmin).map((role) => {
                const on = selected.includes(role);
                return (
                    <button key={role} type="button" className={`ld-chip${on ? ' -on' : ''}`} aria-pressed={on}
                            onClick={() => toggle(role)} disabled={saving}>
                        {on && <Check size={11} weight="bold" aria-hidden="true"/>}
                        {t(`learn_dashbo.profile.${role}`)}
                    </button>
                );
            })}
        </span>
    );

    if (chosen) {
        return (
            <div className="ld-profile" role="group" aria-label={t('learn_dashbo.profile.label')}>
                <span className="ld-profile__label">{t('learn_dashbo.profile.label')}</span>
                {chips}
            </div>
        );
    }

    return (
        <div className="ld-profile -ask" role="group" aria-labelledby="ld-profile-question">
            <p id="ld-profile-question" className="ld-profile__question">{t('learn_dashbo.profile.question')}</p>
            <div className="ld-profile__row">
                {chips}
                <button type="button" className="ld-btn -primary" onClick={() => onSave(selected)}
                        disabled={saving || selected.length === 0}>
                    {t('learn_dashbo.profile.save')}
                </button>
            </div>
        </div>
    );
}
